/* eslint-disable consistent-return */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import * as R from 'ramda';
import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';
import Container from '../common/components/Container/Container';
import Button, { ButtonStyle } from '../common/components/Button/Button';
import ButtonContainer from '../common/components/Button/ButtonContainer';
import TitleContainer from '../common/components/TitleContainer/TitleContainer';
import { FlowConfigContext } from '../providers/FlowConfigContext';
import { CredentialShareContext, ShareStatus } from '../providers/CredentialShareContext';
import { Routes } from '../config/routes';
import withRouteAnalytics from '../services/analytics/withRouteAnalytics';
import { GatekeeperNetworkServiceContext } from '../providers/GatekeeperNetworkService/GatekeeperNetworkServiceContext';
import { Screens } from '../services/analytics/screens';
import { ValidationContext } from '../providers/ValidationContext';

function SharePersonalDetailsScreen(): JSX.Element {
  const { initialized, networkName } = useContext(FlowConfigContext);
  const { gknServiceData } = useContext(GatekeeperNetworkServiceContext);
  const { deleteVerification } = useContext(ValidationContext);
  const partnerName: string = R.propOr(false, 'partnerName', gknServiceData);
  const { getCredentials, handleCredentialsShare, claims, shareStatus } = useContext(CredentialShareContext);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const fetchCredentials = async (abortController?: AbortController) => {
    setLoading(true);
    const credentialsResult = await getCredentials();
    if (abortController?.signal.aborted) {
      return;
    }

    setLoading(false);
    credentialsResult.logError('Unexpected error getting credentials');
    if (credentialsResult.isFailed()) {
      navigate(Routes.VALIDATION_ERROR);
    }
  };

  const handleCredentialsSharing = useCallback(async (abortController: AbortController) => {
    setLoading(true);
    const shareResult = await handleCredentialsShare(abortController);
    if (abortController.signal.aborted || shareResult === undefined) return;
    if (shareResult.isFailed()) {
      shareResult.logError('unexpected error finalizing flow');
      setLoading(false);
      navigate(Routes.VALIDATION_ERROR);
    }
    setLoading(false);
  }, []);

  const handleShareStatusFailed = useCallback(async () => {
    const shareResult = await deleteVerification(false, false);
    if (shareResult.isFailed()) {
      shareResult.logError('unexpected error deleting verification');
    }
    navigate(Routes.VALIDATION_REJECTED);
  }, []);

  useEffect(() => {
    if (!initialized) {
      return;
    }

    const abortController = new AbortController();
    fetchCredentials(abortController);
    return () => abortController.abort();
  }, [initialized]);

  useEffect(() => {
    if (shareStatus === ShareStatus.failed) {
      handleShareStatusFailed();
    }
  }, [initialized, shareStatus]);

  const authorize = async () => {
    const abortController = new AbortController();
    await handleCredentialsSharing(abortController);
    navigate(Routes.VALIDATION_FINALIZING);
  };

  return (
    <Container>
      <TitleContainer center>
        <h1 style={{ fontWeight: 'normal' }} data-testid="SHARE_PERSONAL_DETAILS_SCREEN">
          {t('screens.authorizePersonalInfo.title')}
          <span className="font-bold whitespace-nowrap">{partnerName}</span>
        </h1>
      </TitleContainer>
      <div className="w-full px-8">
        <div className="mb-4">
          <span className="text-xs text-textTertiary">
            {t('screens.authorizePersonalInfo.idLabel', { context: networkName })}
          </span>
          <div className="flex flex-col">
            <span className="mr-2 font-extrabold text-textParagraph">
              {t('screens.authorizePersonalInfo.governmentLabel', { context: networkName })}
            </span>
            <ul className="list-outside list-disc ml-6">
              <li className="text-primary">
                <span className="text-xs text-textTertiary">
                  {t('screens.authorizePersonalInfo.documentRequireInfo', { context: networkName })}
                </span>
              </li>
              <li className="text-primary">
                <span className="text-xs text-textTertiary">
                  {t('screens.authorizePersonalInfo.documentRequirePhotos', { context: networkName })}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-4">
          {claims?.email && (
            <>
              <span className="text-xs text-textTertiary">
                {t('screens.authorizePersonalInfo.emailLabel', { context: networkName })}
              </span>
              <div>
                <span className="text-textParagraph font-extrabold">
                  {claims?.email?.username}@{claims?.email?.domain.name}.{claims?.email?.domain.tld}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <ButtonContainer bottomMargin="mb-2 mt-4">
        <Button
          style={ButtonStyle.DEFAULT}
          disabled={loading}
          loading={loading}
          dataTestid="CONFIRM_SHARE_BUTTON"
          onClick={authorize}
        >
          <span>{t('screens.authorizePersonalInfo.continueButton', { context: networkName })}</span>
        </Button>
      </ButtonContainer>
    </Container>
  );
}
export default withRouteAnalytics(SharePersonalDetailsScreen, Screens.APPROVE_COLLECTED_PII_SHARING);
